import React from "react";
import "@react-pdf-viewer/core/lib/styles/index.css";
import Draggable from "react-draggable";
import { Typography } from "@material-ui/core";
import { BoundsInInches, FieldModel, Group } from "./types";
import { groupColorService } from "./groupColorService";
import CloseIcon from "@material-ui/icons/Close";
import TextIcon from "@material-ui/icons/TextFields";
import DateRangeIcon from "@material-ui/icons/DateRange";
import DialpadIcon from "@material-ui/icons/Dialpad";
import DehazeIcon from "@material-ui/icons/Dehaze";
import CheckBoxIcon from "@material-ui/icons/CheckBox";
import RadioButtonCheckedIcon from "@material-ui/icons/RadioButtonChecked";
import { Resizable } from "re-resizable";
import { typeOfDocumentsLabels } from "../utils/typeOfDocuments";
import BorderColorIcon from "@material-ui/icons/BorderColor";
import EditIcon from "@material-ui/icons/Edit";

export const fieldTypes = [
	{
		label: "Short Text",
		value: "shortText",
		icon: <TextIcon  style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Date",
		value: "date",
		icon: <DateRangeIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.date,
	},
	{
		label: "Number",
		value: "number",
		icon: <DialpadIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.text,
	},
	{
		label: "Select",
		value: "select",
		icon: <DehazeIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.select,
	},
	{
		label: "Checkbox",
		value: "checkbox",
		icon: <CheckBoxIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.checkbox,
	},
	{
		label: "Radio",
		value: "radio",
		icon: <RadioButtonCheckedIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.radio,
	},
	{
		label: "Signature",
		value: "signature",
		icon: <BorderColorIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.signature,
	},
	{
		label: "Initials",
		value: "initials",
		icon: <EditIcon style={{ color: "rgba(0, 106, 198, 1)" }} width={10} height={10} fontSize="small" />,
		type: typeOfDocumentsLabels.initials,
	},
];
export function createFieldsPlugin(
	fields: FieldModel[],
	onMove: (id: string, newBounds: Partial<BoundsInInches>) => void,
	onSelect: (id: string) => void,
	selectedId: string | null,
	onContextMenu?: (event: React.MouseEvent, fieldIndex: number, fieldId?: string) => void,
	groups?: Group[],
	placementMode?: {
		enabled: boolean;
		onPlace: (payload: { page: number; x: number; y: number }) => void;
		cursor?: React.CSSProperties["cursor"];
	},
	highlightedFieldId?: string | null
) {
	return {
		renderPageLayer(props: any) {
			const { pageIndex, width: pageWidth, height: pageHeight } = props;

			// Calculate pixels per inch based on the actual page width (8.5 inches)
			const ppi = pageWidth / 8.5; // px por pulgada (escala actual)

			const pageFields = fields.filter((f) => f.bounds.page === pageIndex + 1);

			return (
				<div
					style={{
						position: "relative",
						width: pageWidth,
						height: pageHeight,
					}}
				>
					{placementMode?.enabled && (
						<div
							role="button"
							tabIndex={0}
							aria-label="Click to place field"
							style={{
								position: "absolute",
								inset: 0,
								zIndex: 2000,
								cursor: placementMode.cursor ?? "crosshair",
								background: "transparent",
							}}
							onMouseDown={(e) => {
								// Prevent text selection / viewer interactions while placing
								e.preventDefault();
								e.stopPropagation();
							}}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();

								// Compute click point relative to this page layer
								const rect = (e.currentTarget as HTMLDivElement).getBoundingClientRect();
								const relX = e.clientX - rect.left;
								const relY = e.clientY - rect.top;

								// Convert pixels -> inches
								const xIn = relX / ppi;
								const yIn = relY / ppi;

								placementMode.onPlace({
									page: pageIndex + 1,
									x: xIn,
									y: yIn,
								});
							}}
							onKeyDown={(e) => {
								// Keep focusable for accessibility; actual cancel handling lives in parent
								if (e.key === "Enter" || e.key === " ") {
									e.preventDefault();
								}
							}}
						>
							{/* lightweight hint without blocking click */}
							<div
								style={{
									position: "absolute",
									top: 12,
									left: 12,
									padding: "6px 10px",
									background: "rgba(33, 33, 33, 0.72)",
									color: "white",
									borderRadius: 6,
									fontSize: 12,
									pointerEvents: "none",
									maxWidth: "70%",
								}}
							>
								Click to place field • Press Esc to cancel
							</div>
						</div>
					)}
					{pageFields.map((f) => {
						const { x, y, width, height } = f.bounds;

						// Convert inches to pixels
						const left = x * ppi;
						const top = y * ppi;
						const w = width * ppi;
						const h = height * ppi;

           const iconField = fieldTypes.find(field => field.value === f.type)?.icon;

						// Get group color from persisted fieldGroupColor or fallback to group lookup
						let groupColorScheme = null;
						if (f.fieldGroupColor) {
							// Use persisted color directly
							groupColorScheme = groupColorService.getColorByPrimaryColor(f.fieldGroupColor);
						} else if (f.fieldGroup && groups) {
							// Fallback: lookup from groups array
							const assignedGroup = groups.find(g => g.name === f.fieldGroup);
							if (assignedGroup) {
								groupColorScheme = groupColorService.getGroupColor(assignedGroup);
							}
						}

						return (
							<Draggable
								key={f.id}
								bounds="parent"
								position={{ x: left, y: top }}
								onStart={(e) => e.stopPropagation()}
								onDrag={(e, data) => {
									/* opcional: podrías hacer 
                     live preview llamando onMove aquí */
								}}
								onStop={(e, data) => {
									const newX = data.x / ppi;
									const newY = data.y / ppi;
									onMove(f.id, { x: newX, y: newY });
								}}
							>
								<Resizable
									size={{ width: w, height: h }}
									onResizeStart={(_, __, ___) => /* evita bubbling */ {}}
									onResizeStop={(_, __, ref, delta) => {
										const newW = (w + delta.width) / ppi;
										const newH = (h + delta.height) / ppi;
										onMove(f.id, { width: newW, height: newH });
									}}
									style={{
										position: "absolute",
										outline: (() => {
											// Priority: Selected field -> Group assignment -> Default
											if (f.id === selectedId) {
												return "2px solid #ff9800"; // Orange for selected field
											} else if (f.id === highlightedFieldId) {
												return "2px solid #2196f3"; // Blue highlight for newly added field
											} else if (groupColorScheme) {
												return `2px solid ${groupColorScheme.border}`; // Group color
											}
											return "1px solid #ff9800"; // Orange solid border
										})(),
										boxSizing: "border-box",
										background: (() => {
											// Priority: Selected field -> Group assignment -> Default
											if (f.id === selectedId) {
												return "rgba(255, 152, 0, 0.5)"; // Orange for selected field
											} else if (f.id === highlightedFieldId) {
												return "rgba(33, 150, 243, 0.18)"; // Subtle blue highlight
											} else if (groupColorScheme) {
												return groupColorScheme.secondary; // Group secondary color
											}
											return 'white'; // Default white background
										})(),
										zIndex: 1000,
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										cursor: f.id === selectedId ? "move" : "default",
										transition: f.id === highlightedFieldId ? "outline-color 200ms ease, background-color 200ms ease" : undefined,
									}}
									enable={{
										top: false,
										bottom: false,
										left: false,
										right: false,
										topRight: true,
										bottomRight: true,
										bottomLeft: true,
										topLeft: true,
									}}
								>
									<div
										style={{
											width: "100%",
											height: "100%",
											display: "flex",
											alignItems: "center",
											justifyContent: "center",
											color: f.id === selectedId ? "white" : "rgba(0, 106, 198, 1)",
										}}
										onClick={(e) => {
											e.stopPropagation();
											onSelect(f.id);
										}}
										onContextMenu={(e) => {
											if (onContextMenu) {
												e.preventDefault();
												e.stopPropagation();
												const fieldIndex = fields.findIndex(field => field.id === f.id);
												onContextMenu(e, fieldIndex, f.id);
											}
										}}
										title={f.title}
									>
										<span style={{ display: "flex", alignItems: "center", justifyContent: "center", fontSize: "8px" }}>
										 {iconField}	{f.title}
										</span>
									</div>
								</Resizable>
							</Draggable>
						);
					})}
				</div>
			);
		},
	};
}
